import {
  ColumnConfig,
  TablePreferences,
  DEFAULT_COLUMNS,
  PREFERENCES_VERSION,
} from '../types/columns';
import { trackError } from '../services/rumService';

const STORAGE_KEY = 'projectTableColumns';

export const saveColumnPreferences = (columns: ColumnConfig[]): void => {
  try {
    const preferences: TablePreferences = {
      columns,
      version: PREFERENCES_VERSION,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.warn('Failed to save column preferences to localStorage:');
    if (error instanceof Error) {
      trackError(error, 'columnStorage', {
        operation: 'saveColumnPreferences',
        columnsCount: columns.length,
      });
    }
  }
};

export const loadColumnPreferences = (): ColumnConfig[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return DEFAULT_COLUMNS;
    }

    const preferences: TablePreferences = JSON.parse(stored);

    // Version check for future compatibility - if version mismatch, merge with defaults
    if (!preferences.version || preferences.version !== PREFERENCES_VERSION) {
      console.info(
        'Column preferences version mismatch, merging with updated defaults'
      );
      return validateAndMergeColumns(preferences.columns);
    }

    // Validate the structure and merge with defaults if needed
    const validatedColumns = validateAndMergeColumns(preferences.columns);
    return validatedColumns;
  } catch (error) {
    console.warn('Failed to load column preferences from localStorage:');
    if (error instanceof Error) {
      trackError(error, 'columnStorage', {
        operation: 'loadColumnPreferences',
      });
    }
    return DEFAULT_COLUMNS;
  }
};

export const resetColumnPreferences = (): ColumnConfig[] => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return DEFAULT_COLUMNS;
  } catch (error) {
    console.warn('Failed to reset column preferences:');
    if (error instanceof Error) {
      trackError(error, 'columnStorage', {
        operation: 'resetColumnPreferences',
      });
    }
    return DEFAULT_COLUMNS;
  }
};

const validateAndMergeColumns = (
  storedColumns: ColumnConfig[]
): ColumnConfig[] => {
  // Create a map of stored columns by ID
  const storedMap = new Map(storedColumns.map(col => [col.id, col]));

  // Start with default columns and update with stored preferences
  return DEFAULT_COLUMNS.map(defaultCol => {
    const stored = storedMap.get(defaultCol.id);
    if (stored) {
      // Preserve the stored visibility state but keep other defaults
      return {
        ...defaultCol,
        visible: stored.visible,
      };
    }
    return defaultCol;
  });
};
